import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus,
  Pencil,
  Trash2,
  X,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Settings,
  Upload,
  ImageIcon,
  // Icon options for configurator
  Circle,
  Square,
  Triangle,
  Star,
  Heart,
  Shield,
  Zap,
  Flame,
  Droplets,
  Wind,
  Sun,
  Moon,
  Cloud,
  Anchor,
  Ship,
  Compass,
  Navigation,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Check,
  AlertTriangle,
  Info,
  HelpCircle,
  Settings2,
  Wrench,
  Hammer,
  Gauge,
  Thermometer,
  Ruler,
  Box,
  Package,
  Layers,
  Grid3X3,
  LayoutGrid,
  type LucideIcon,
} from 'lucide-react'

// Available icons for option selection
const AVAILABLE_ICONS: { name: string; icon: LucideIcon }[] = [
  { name: 'circle', icon: Circle },
  { name: 'square', icon: Square },
  { name: 'triangle', icon: Triangle },
  { name: 'star', icon: Star },
  { name: 'heart', icon: Heart },
  { name: 'shield', icon: Shield },
  { name: 'zap', icon: Zap },
  { name: 'flame', icon: Flame },
  { name: 'droplets', icon: Droplets },
  { name: 'wind', icon: Wind },
  { name: 'sun', icon: Sun },
  { name: 'moon', icon: Moon },
  { name: 'cloud', icon: Cloud },
  { name: 'anchor', icon: Anchor },
  { name: 'ship', icon: Ship },
  { name: 'compass', icon: Compass },
  { name: 'navigation', icon: Navigation },
  { name: 'lock', icon: Lock },
  { name: 'unlock', icon: Unlock },
  { name: 'eye', icon: Eye },
  { name: 'eye-off', icon: EyeOff },
  { name: 'check', icon: Check },
  { name: 'alert-triangle', icon: AlertTriangle },
  { name: 'info', icon: Info },
  { name: 'help-circle', icon: HelpCircle },
  { name: 'settings', icon: Settings2 },
  { name: 'wrench', icon: Wrench },
  { name: 'hammer', icon: Hammer },
  { name: 'gauge', icon: Gauge },
  { name: 'thermometer', icon: Thermometer },
  { name: 'ruler', icon: Ruler },
  { name: 'box', icon: Box },
  { name: 'package', icon: Package },
  { name: 'layers', icon: Layers },
  { name: 'grid', icon: Grid3X3 },
  { name: 'layout-grid', icon: LayoutGrid },
]

// Helper to get icon component by name
export const getIconByName = (name: string | null): LucideIcon | null => {
  if (!name) return null
  const found = AVAILABLE_ICONS.find((i) => i.name === name)
  return found?.icon || null
}
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { supabase, uploadFile, getPublicUrl } from '@/lib/supabase'
import type {
  ConfiguratorTemplate,
  ConfiguratorTemplateField,
  ConfiguratorFieldType,
} from '@/types'

// Sortable Field Item Component
function SortableFieldItem({
  field,
  onEdit,
  onDelete,
  isExpanded,
  onToggleExpand,
}: {
  field: ConfiguratorTemplateField
  onEdit: () => void
  onDelete: () => void
  isExpanded: boolean
  onToggleExpand: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const fieldTypeLabels: Record<ConfiguratorFieldType, string> = {
    dropdown: 'Dropdown',
    text: 'Text Input',
    number: 'Number Input',
    checkbox: 'Checkbox',
    radio: 'Radio Buttons',
  }

  return (
    <div ref={setNodeRef} style={style} className="border border-secondary-200 rounded-lg bg-white">
      <div className="flex items-center gap-3 p-3">
        <button {...attributes} {...listeners} className="cursor-grab touch-none">
          <GripVertical className="w-5 h-5 text-secondary-400" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-secondary-900">{field.label}</span>
            <span className="text-xs px-2 py-0.5 bg-secondary-100 text-secondary-600 rounded">
              {fieldTypeLabels[field.field_type]}
            </span>
            {field.is_required && (
              <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded">Required</span>
            )}
          </div>
          <span className="text-sm text-secondary-500">{field.field_key}</span>
        </div>
        <div className="flex items-center gap-1">
          {(field.field_type === 'dropdown' || field.field_type === 'radio') && (
            <button
              onClick={onToggleExpand}
              className="p-2 hover:bg-secondary-100 rounded-lg text-secondary-600"
              title="Show options"
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={onEdit}
            className="p-2 hover:bg-secondary-100 rounded-lg text-secondary-600"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 hover:bg-red-50 rounded-lg text-red-500"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {isExpanded && field.options && field.options.length > 0 && (
        <div className="border-t border-secondary-100 p-3 bg-secondary-50">
          <div className="text-xs font-medium text-secondary-500 mb-2">OPTIONS:</div>
          <div className="flex flex-wrap gap-2">
            {field.options
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((opt) => {
                const IconComponent = getIconByName(opt.icon)
                return (
                  <span
                    key={opt.id}
                    className={`text-sm px-2 py-1 rounded flex items-center gap-1.5 ${
                      opt.is_default
                        ? 'bg-primary-100 text-primary-700 border border-primary-200'
                        : 'bg-white border border-secondary-200 text-secondary-700'
                    }`}
                  >
                    {opt.icon_url ? (
                      <img src={opt.icon_url} alt="" className="w-4 h-4 object-contain" />
                    ) : IconComponent ? (
                      <IconComponent className="w-4 h-4" />
                    ) : null}
                    {opt.label}
                    {opt.is_default && ' (default)'}
                  </span>
                )
              })}
          </div>
        </div>
      )}
    </div>
  )
}

export function ConfiguratorTemplatesPage() {
  const queryClient = useQueryClient()
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false)
  const [isFieldModalOpen, setIsFieldModalOpen] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<ConfiguratorTemplate | null>(null)
  const [selectedField, setSelectedField] = useState<ConfiguratorTemplateField | null>(null)
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set())

  // Form states
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    show_unit_selector: true,
    show_dimensions: true,
    show_size_reference: true,
  })

  const [fieldForm, setFieldForm] = useState({
    field_type: 'dropdown' as ConfiguratorFieldType,
    field_key: '',
    label: '',
    placeholder: '',
    help_text: '',
    is_required: false,
    options: [] as { value: string; label: string; icon: string | null; icon_url: string | null; is_default: boolean }[],
  })

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  // Fetch templates
  const { data: templates, isLoading } = useQuery({
    queryKey: ['configurator-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('configurator_templates')
        .select(`
          *,
          fields:configurator_template_fields(
            *,
            options:configurator_field_options(*)
          )
        `)
        .order('name')
      if (error) throw error
      return data as ConfiguratorTemplate[]
    },
  })

  // Create/Update template mutation
  const templateMutation = useMutation({
    mutationFn: async (data: typeof templateForm & { id?: string }) => {
      if (data.id) {
        const { error } = await supabase
          .from('configurator_templates')
          .update({
            name: data.name,
            description: data.description || null,
            show_unit_selector: data.show_unit_selector,
            show_dimensions: data.show_dimensions,
            show_size_reference: data.show_size_reference,
          })
          .eq('id', data.id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('configurator_templates').insert({
          name: data.name,
          description: data.description || null,
          show_unit_selector: data.show_unit_selector,
          show_dimensions: data.show_dimensions,
          show_size_reference: data.show_size_reference,
        })
        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configurator-templates'] })
      setIsTemplateModalOpen(false)
      resetTemplateForm()
    },
  })

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('configurator_templates').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configurator-templates'] })
    },
  })

  // Create/Update field mutation
  const fieldMutation = useMutation({
    mutationFn: async (data: typeof fieldForm & { id?: string; template_id: string }) => {
      let fieldId = data.id

      if (data.id) {
        // Update field
        const { error } = await supabase
          .from('configurator_template_fields')
          .update({
            field_type: data.field_type,
            field_key: data.field_key,
            label: data.label,
            placeholder: data.placeholder || null,
            help_text: data.help_text || null,
            is_required: data.is_required,
          })
          .eq('id', data.id)
        if (error) throw error

        // Delete existing options
        await supabase.from('configurator_field_options').delete().eq('field_id', data.id)
      } else {
        // Get max sort order
        const { data: existingFields } = await supabase
          .from('configurator_template_fields')
          .select('sort_order')
          .eq('template_id', data.template_id)
          .order('sort_order', { ascending: false })
          .limit(1)

        const maxSortOrder = existingFields?.[0]?.sort_order ?? -1

        // Create field
        const { data: newField, error } = await supabase
          .from('configurator_template_fields')
          .insert({
            template_id: data.template_id,
            field_type: data.field_type,
            field_key: data.field_key,
            label: data.label,
            placeholder: data.placeholder || null,
            help_text: data.help_text || null,
            is_required: data.is_required,
            sort_order: maxSortOrder + 1,
          })
          .select()
          .single()
        if (error) throw error
        fieldId = newField.id
      }

      // Insert options if dropdown or radio
      if ((data.field_type === 'dropdown' || data.field_type === 'radio') && data.options.length > 0) {
        const { error: optError } = await supabase.from('configurator_field_options').insert(
          data.options.map((opt, idx) => ({
            field_id: fieldId,
            value: opt.value,
            label: opt.label,
            icon: opt.icon || null,
            icon_url: opt.icon_url || null,
            is_default: opt.is_default,
            sort_order: idx,
          }))
        )
        if (optError) throw optError
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configurator-templates'] })
      setIsFieldModalOpen(false)
      resetFieldForm()
    },
  })

  // Delete field mutation
  const deleteFieldMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('configurator_template_fields').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configurator-templates'] })
    },
  })

  // Reorder fields mutation
  const reorderFieldsMutation = useMutation({
    mutationFn: async (fields: { id: string; sort_order: number }[]) => {
      for (const field of fields) {
        await supabase
          .from('configurator_template_fields')
          .update({ sort_order: field.sort_order })
          .eq('id', field.id)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configurator-templates'] })
    },
  })

  const resetTemplateForm = () => {
    setTemplateForm({
      name: '',
      description: '',
      show_unit_selector: true,
      show_dimensions: true,
      show_size_reference: true,
    })
    setSelectedTemplate(null)
  }

  const resetFieldForm = () => {
    setFieldForm({
      field_type: 'dropdown',
      field_key: '',
      label: '',
      placeholder: '',
      help_text: '',
      is_required: false,
      options: [],
    })
    setSelectedField(null)
  }

  const handleEditTemplate = (template: ConfiguratorTemplate) => {
    setSelectedTemplate(template)
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      show_unit_selector: template.show_unit_selector,
      show_dimensions: template.show_dimensions,
      show_size_reference: template.show_size_reference,
    })
    setIsTemplateModalOpen(true)
  }

  const handleEditField = (field: ConfiguratorTemplateField) => {
    setSelectedField(field)
    setFieldForm({
      field_type: field.field_type,
      field_key: field.field_key,
      label: field.label,
      placeholder: field.placeholder || '',
      help_text: field.help_text || '',
      is_required: field.is_required,
      options:
        field.options?.map((o) => ({
          value: o.value,
          label: o.label,
          icon: o.icon || null,
          icon_url: o.icon_url || null,
          is_default: o.is_default,
        })) || [],
    })
    setIsFieldModalOpen(true)
  }

  const handleFieldDragEnd = (event: DragEndEvent, template: ConfiguratorTemplate) => {
    const { active, over } = event
    if (!over || active.id === over.id || !template.fields) return

    const oldIndex = template.fields.findIndex((f) => f.id === active.id)
    const newIndex = template.fields.findIndex((f) => f.id === over.id)
    const reordered = arrayMove(template.fields, oldIndex, newIndex)

    reorderFieldsMutation.mutate(
      reordered.map((f, idx) => ({ id: f.id, sort_order: idx }))
    )
  }

  const addOption = () => {
    setFieldForm((prev) => ({
      ...prev,
      options: [...prev.options, { value: '', label: '', icon: null, icon_url: null, is_default: false }],
    }))
  }

  const removeOption = (index: number) => {
    setFieldForm((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }))
  }

  const updateOption = (index: number, field: 'value' | 'label' | 'icon' | 'icon_url' | 'is_default', value: string | boolean | null) => {
    setFieldForm((prev) => ({
      ...prev,
      options: prev.options.map((opt, i) => {
        if (i === index) {
          return { ...opt, [field]: value }
        }
        if (field === 'is_default' && value === true) {
          return { ...opt, is_default: false }
        }
        return opt
      }),
    }))
  }

  const generateFieldKey = (label: string) => {
    return label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Configurator Templates</h1>
          <p className="text-secondary-600">Manage product configurator templates for categories</p>
        </div>
        <button
          onClick={() => {
            resetTemplateForm()
            setIsTemplateModalOpen(true)
          }}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Template
        </button>
      </div>

      {/* Templates List */}
      <div className="space-y-4">
        {templates?.length === 0 && (
          <div className="card p-12 text-center">
            <Settings className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">No templates yet</h3>
            <p className="text-secondary-600 mb-4">Create your first configurator template</p>
            <button
              onClick={() => setIsTemplateModalOpen(true)}
              className="btn-primary"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Template
            </button>
          </div>
        )}

        {templates?.map((template) => (
          <div key={template.id} className="card">
            {/* Template Header */}
            <div className="p-4 border-b border-secondary-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-secondary-900">{template.name}</h3>
                {template.description && (
                  <p className="text-sm text-secondary-500">{template.description}</p>
                )}
                <div className="flex gap-2 mt-2">
                  {template.show_unit_selector && (
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                      Unit Selector
                    </span>
                  )}
                  {template.show_dimensions && (
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                      Dimensions
                    </span>
                  )}
                  {template.show_size_reference && (
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                      Size Reference
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleEditTemplate(template)}
                  className="btn-outline btn-sm"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    if (confirm('Delete this template?')) {
                      deleteTemplateMutation.mutate(template.id)
                    }
                  }}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Fields */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-secondary-700">Custom Fields</h4>
                <button
                  onClick={() => {
                    setSelectedTemplate(template)
                    resetFieldForm()
                    setIsFieldModalOpen(true)
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Field
                </button>
              </div>

              {template.fields && template.fields.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) => handleFieldDragEnd(e, template)}
                >
                  <SortableContext
                    items={template.fields.map((f) => f.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {template.fields
                        .sort((a, b) => a.sort_order - b.sort_order)
                        .map((field) => (
                          <SortableFieldItem
                            key={field.id}
                            field={field}
                            onEdit={() => {
                              setSelectedTemplate(template)
                              handleEditField(field)
                            }}
                            onDelete={() => {
                              if (confirm('Delete this field?')) {
                                deleteFieldMutation.mutate(field.id)
                              }
                            }}
                            isExpanded={expandedFields.has(field.id)}
                            onToggleExpand={() => {
                              setExpandedFields((prev) => {
                                const next = new Set(prev)
                                if (next.has(field.id)) {
                                  next.delete(field.id)
                                } else {
                                  next.add(field.id)
                                }
                                return next
                              })
                            }}
                          />
                        ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <p className="text-sm text-secondary-500 text-center py-4">
                  No custom fields added yet
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Template Modal */}
      <AnimatePresence>
        {isTemplateModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setIsTemplateModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-secondary-100">
                <h3 className="text-lg font-semibold text-secondary-900">
                  {selectedTemplate ? 'Edit Template' : 'New Template'}
                </h3>
                <button
                  onClick={() => setIsTemplateModalOpen(false)}
                  className="p-2 hover:bg-secondary-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm((p) => ({ ...p, name: e.target.value }))}
                    className="input"
                    placeholder="e.g., Door Configurator"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={templateForm.description}
                    onChange={(e) => setTemplateForm((p) => ({ ...p, description: e.target.value }))}
                    className="input resize-none"
                    rows={2}
                    placeholder="Optional description"
                  />
                </div>

                <div className="border-t border-secondary-100 pt-4">
                  <h4 className="text-sm font-medium text-secondary-900 mb-3">Built-in Fields</h4>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={templateForm.show_unit_selector}
                        onChange={(e) =>
                          setTemplateForm((p) => ({ ...p, show_unit_selector: e.target.checked }))
                        }
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-sm text-secondary-700">Show Unit Selector (mm/inches)</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={templateForm.show_dimensions}
                        onChange={(e) =>
                          setTemplateForm((p) => ({ ...p, show_dimensions: e.target.checked }))
                        }
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-sm text-secondary-700">Show Width & Height inputs</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={templateForm.show_size_reference}
                        onChange={(e) =>
                          setTemplateForm((p) => ({ ...p, show_size_reference: e.target.checked }))
                        }
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-sm text-secondary-700">Show Size Reference selector</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 p-4 border-t border-secondary-100">
                <button onClick={() => setIsTemplateModalOpen(false)} className="btn-outline">
                  Cancel
                </button>
                <button
                  onClick={() =>
                    templateMutation.mutate({
                      ...templateForm,
                      id: selectedTemplate?.id,
                    })
                  }
                  disabled={!templateForm.name || templateMutation.isPending}
                  className="btn-primary"
                >
                  {templateMutation.isPending ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Field Modal */}
      <AnimatePresence>
        {isFieldModalOpen && selectedTemplate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setIsFieldModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-secondary-100 sticky top-0 bg-white">
                <h3 className="text-lg font-semibold text-secondary-900">
                  {selectedField ? 'Edit Field' : 'Add Field'}
                </h3>
                <button
                  onClick={() => setIsFieldModalOpen(false)}
                  className="p-2 hover:bg-secondary-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Field Type *
                  </label>
                  <select
                    value={fieldForm.field_type}
                    onChange={(e) =>
                      setFieldForm((p) => ({
                        ...p,
                        field_type: e.target.value as ConfiguratorFieldType,
                      }))
                    }
                    className="input"
                  >
                    <option value="dropdown">Dropdown</option>
                    <option value="text">Text Input</option>
                    <option value="number">Number Input</option>
                    <option value="checkbox">Checkbox</option>
                    <option value="radio">Radio Buttons</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Label *
                  </label>
                  <input
                    type="text"
                    value={fieldForm.label}
                    onChange={(e) => {
                      const label = e.target.value
                      setFieldForm((p) => ({
                        ...p,
                        label,
                        field_key: p.field_key || generateFieldKey(label),
                      }))
                    }}
                    className="input"
                    placeholder="e.g., Material"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Field Key *
                  </label>
                  <input
                    type="text"
                    value={fieldForm.field_key}
                    onChange={(e) => setFieldForm((p) => ({ ...p, field_key: e.target.value }))}
                    className="input"
                    placeholder="e.g., material"
                  />
                  <p className="text-xs text-secondary-500 mt-1">
                    Unique identifier (lowercase, no spaces)
                  </p>
                </div>

                {(fieldForm.field_type === 'text' || fieldForm.field_type === 'number') && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Placeholder
                    </label>
                    <input
                      type="text"
                      value={fieldForm.placeholder}
                      onChange={(e) => setFieldForm((p) => ({ ...p, placeholder: e.target.value }))}
                      className="input"
                      placeholder="e.g., Enter value..."
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Help Text
                  </label>
                  <input
                    type="text"
                    value={fieldForm.help_text}
                    onChange={(e) => setFieldForm((p) => ({ ...p, help_text: e.target.value }))}
                    className="input"
                    placeholder="Optional help tooltip"
                  />
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fieldForm.is_required}
                    onChange={(e) => setFieldForm((p) => ({ ...p, is_required: e.target.checked }))}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm text-secondary-700">This field is required</span>
                </label>

                {/* Options for dropdown/radio */}
                {(fieldForm.field_type === 'dropdown' || fieldForm.field_type === 'radio') && (
                  <div className="border-t border-secondary-100 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-secondary-900">Options</h4>
                      <button
                        type="button"
                        onClick={addOption}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Option
                      </button>
                    </div>

                    <div className="space-y-3">
                      {fieldForm.options.map((opt, idx) => {
                        const SelectedIcon = opt.icon ? getIconByName(opt.icon) : null
                        return (
                          <div key={idx} className="p-3 border border-secondary-200 rounded-lg bg-white space-y-3">
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={opt.value}
                                onChange={(e) => updateOption(idx, 'value', e.target.value)}
                                className="input flex-1"
                                placeholder="Value"
                              />
                              <input
                                type="text"
                                value={opt.label}
                                onChange={(e) => updateOption(idx, 'label', e.target.value)}
                                className="input flex-1"
                                placeholder="Label"
                              />
                              <label className="flex items-center gap-1 text-sm whitespace-nowrap">
                                <input
                                  type="radio"
                                  name="default_option"
                                  checked={opt.is_default}
                                  onChange={() => updateOption(idx, 'is_default', true)}
                                  className="w-4 h-4"
                                />
                                Default
                              </label>
                              <button
                                type="button"
                                onClick={() => removeOption(idx)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>

                            {/* Image Upload or Icon Selection */}
                            <div className="border-t border-secondary-100 pt-3">
                              <div className="flex items-center gap-4 mb-2">
                                <span className="text-xs font-medium text-secondary-700">Icon/Image (optional):</span>
                              </div>

                              {/* Custom Image Upload */}
                              <div className="flex items-center gap-3 mb-3">
                                {opt.icon_url ? (
                                  <div className="relative">
                                    <img
                                      src={opt.icon_url}
                                      alt=""
                                      className="w-10 h-10 object-contain rounded border border-secondary-200"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => updateOption(idx, 'icon_url', null)}
                                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ) : (
                                  <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-secondary-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors">
                                    <Upload className="w-4 h-4 text-secondary-400" />
                                    <span className="text-xs text-secondary-600">Upload Image</span>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="sr-only"
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0]
                                        if (file) {
                                          try {
                                            const path = `configurator-icons/${Date.now()}-${file.name}`
                                            await uploadFile('gallery', path, file)
                                            const url = getPublicUrl('gallery', path)
                                            updateOption(idx, 'icon_url', url)
                                            // Clear lucide icon when custom image is uploaded
                                            updateOption(idx, 'icon', null)
                                          } catch (err) {
                                            console.error('Upload failed:', err)
                                          }
                                        }
                                      }}
                                    />
                                  </label>
                                )}
                                {opt.icon_url && (
                                  <span className="text-xs text-green-600 flex items-center gap-1">
                                    <ImageIcon className="w-3 h-3" />
                                    Custom image
                                  </span>
                                )}
                              </div>

                              {/* Or use Lucide Icon */}
                              {!opt.icon_url && (
                                <>
                                  <div className="text-xs text-secondary-500 mb-2">Or select an icon:</div>
                                  <div className="flex items-center gap-1 flex-wrap">
                                    <button
                                      type="button"
                                      onClick={() => updateOption(idx, 'icon', null)}
                                      className={`p-1.5 rounded border ${
                                        !opt.icon
                                          ? 'border-primary-500 bg-primary-50'
                                          : 'border-secondary-200 hover:border-secondary-300'
                                      }`}
                                      title="No icon"
                                    >
                                      <X className="w-4 h-4 text-secondary-400" />
                                    </button>
                                    {AVAILABLE_ICONS.map((iconItem) => {
                                      const IconComp = iconItem.icon
                                      return (
                                        <button
                                          key={iconItem.name}
                                          type="button"
                                          onClick={() => updateOption(idx, 'icon', iconItem.name)}
                                          className={`p-1.5 rounded border ${
                                            opt.icon === iconItem.name
                                              ? 'border-primary-500 bg-primary-50'
                                              : 'border-secondary-200 hover:border-secondary-300'
                                          }`}
                                          title={iconItem.name}
                                        >
                                          <IconComp className="w-4 h-4" />
                                        </button>
                                      )
                                    })}
                                  </div>
                                  {SelectedIcon && (
                                    <div className="flex items-center gap-2 text-xs text-secondary-500 mt-2">
                                      <SelectedIcon className="w-4 h-4" />
                                      <span>Selected: {opt.icon}</span>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>

                    {fieldForm.options.length === 0 && (
                      <p className="text-sm text-secondary-500 text-center py-2">
                        No options added yet
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 p-4 border-t border-secondary-100 sticky bottom-0 bg-white">
                <button onClick={() => setIsFieldModalOpen(false)} className="btn-outline">
                  Cancel
                </button>
                <button
                  onClick={() =>
                    fieldMutation.mutate({
                      ...fieldForm,
                      id: selectedField?.id,
                      template_id: selectedTemplate.id,
                    })
                  }
                  disabled={!fieldForm.label || !fieldForm.field_key || fieldMutation.isPending}
                  className="btn-primary"
                >
                  {fieldMutation.isPending ? 'Saving...' : 'Save Field'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
